import { requireConnection } from "./connection";
// Extracted from TranslatePage so the OCR pipeline can be unit tested
// independently of the React/DOM upload UI.
import { grayscaleAndStretchContrast } from "./imagePreprocessing";
import { recognizeJapaneseTextViaCloud } from "./ocrCloud";
import { recognizeJapaneseTextViaLlm } from "./ocrLlm";

// Tesseract's own MeanTextConf (0-100) below this is treated as unreliable -
// still shown to the user (better than nothing, and often still useful),
// but flagged rather than silently presented as trustworthy.
const LOW_CONFIDENCE_THRESHOLD = 60;

export interface OcrResult {
    text: string;
    confidence: number;
    lowConfidence: boolean;
    // Present (true) only when the AI-assisted second opinion produced the
    // text, so the UI can say "with AI assistance" only when that is true.
    aiAssisted?: boolean;
}

export function isLowConfidence(confidence: number): boolean {
    return confidence < LOW_CONFIDENCE_THRESHOLD;
}

// Tesseract's jpn model tends to insert spurious spaces between characters
// that don't belong there - normal Japanese text has none - so each line is
// stripped of internal whitespace. Line breaks themselves are preserved
// (menus commonly put one item per line), and now-empty lines are dropped.
export function cleanOcrText(rawText: string): string {
    return rawText
        .split("\n")
        .map((line) => line.replace(/[ \t]+/g, "").trim())
        .filter((line) => line.length > 0)
        .join("\n");
}

// Cloud Vision doesn't have Tesseract's spurious-space quirk (it's a much
// more linguistically-aware model), so this only trims each line and drops
// now-empty ones - stripping all internal whitespace here could remove
// spacing Vision correctly preserved.
export function cleanCloudOcrText(rawText: string): string {
    return rawText
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .join("\n");
}

// Picks whichever of two same-image OCR passes Tesseract itself is more
// confident in. Exported so the adaptive-PSM strategy below can be tested
// without needing a real worker/canvas.
export function pickBetterResult<T extends { confidence: number }>(a: T, b: T): T {
    return b.confidence >= a.confidence ? b : a;
}

async function loadImageToCanvas(source: File | Blob): Promise<HTMLCanvasElement> {
    const objectUrl = URL.createObjectURL(source);
    try {
        const image = await new Promise<HTMLImageElement>((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error("Couldn't load the image"));
            img.src = objectUrl;
        });

        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Canvas 2D context is unavailable");
        ctx.drawImage(image, 0, 0);
        return canvas;
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
}

// Runs the full pipeline: decode the uploaded image onto a canvas at its
// native resolution (no downscaling - kanji stroke detail is dense enough
// that shrinking tends to hurt more than help), enhance contrast, then hand
// the canvas straight to Tesseract (no lossy re-encoding).
//
// Page segmentation mode is NOT a single static choice. A controlled A/B
// test against a synthetic menu image, at two degradation levels (moderate:
// soft lighting/grain; heavy: added blur/rotation/heavier grain, closer to
// a real handheld photo), showed opposite winners:
//   - moderate degradation: Tesseract's own default PSM scored confidence
//     ~80-82 with every line and all prices intact; forcing PSM.AUTO scored
//     ~65-70 and dropped the "松" character and every price number.
//   - heavy degradation: the default PSM collapsed to ~34-46 confidence with
//     garbage tokens littered through the output (matching real user
//     reports); PSM.AUTO recovered to ~78-82 confidence with every menu-item
//     line intact.
// Neither PSM is uniformly better - which one wins depends on how degraded
// the specific photo is, which can't be known in advance. Both passes reuse
// the same worker (no re-downloading the language model) and Tesseract's
// own mean confidence, which tracked the actually-better output in every
// test run, decides which result to keep.
//
// This is the fallback path - see recognizeJapaneseText below, which tries
// Google Cloud Vision first and only falls back to this client-side
// Tesseract pipeline when that's unavailable. Even tuned, Tesseract still
// struggles with real-world photographed text (stylized fonts, uneven
// lighting) in a way Cloud Vision generally does not.
export async function recognizeJapaneseTextLocally(source: File | Blob): Promise<OcrResult> {
    const canvas = await loadImageToCanvas(source);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D context is unavailable");

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const processed = grayscaleAndStretchContrast(imageData);
    ctx.putImageData(new ImageData(processed.data, processed.width, processed.height), 0, 0);

    const { createWorker, PSM } = await import("tesseract.js");
    const worker = await createWorker("jpn");
    try {
        const defaultPass = await worker.recognize(canvas);

        await worker.setParameters({ tessedit_pageseg_mode: PSM.AUTO });
        const autoPass = await worker.recognize(canvas);

        const picked = pickBetterResult(defaultPass.data, autoPass.data);

        // eslint-disable-next-line no-console
        console.info(
            "[ocr] image %dx%d - default PSM confidence=%d, AUTO PSM confidence=%d - using %s",
            canvas.width,
            canvas.height,
            defaultPass.data.confidence,
            autoPass.data.confidence,
            picked === autoPass.data ? "AUTO" : "default"
        );

        const confidence = picked.confidence;
        return {
            text: cleanOcrText(picked.text),
            confidence,
            lowConfidence: isLowConfidence(confidence),
        };
    } finally {
        await worker.terminate();
    }
}

export interface RecognizeOptions {
    // Only when provided does a genuinely low-confidence result get a second
    // opinion from the AI-assisted OCR fallback (see ocrLlm.ts) - that path
    // requires a signed-in session, so the caller (which owns auth) opts in
    // by passing its own token getter rather than this module reaching into
    // auth state itself.
    getIdToken?: () => Promise<string>;
}

// Tries Google Cloud Vision first for far better accuracy on real-world
// photographed Japanese text, and falls back to the local Tesseract
// pipeline above whenever Cloud Vision is unavailable (network error, the
// backend's Google API key not yet permitted to call the Vision API, quota
// exhausted, etc.) so the feature keeps working either way - the same
// pattern utils/translate.ts already uses for Google Translate vs. MyMemory.
//
// If that result is still low-confidence and the caller opted in via
// getIdToken, one more pass is tried through a multimodal LLM (ocrLlm.ts),
// which reads stylized/decorative fonts better than segmentation-based OCR
// in some cases. That pass is rate-limited server-side and best-effort: any
// failure (not signed in, daily limit reached, network error) just keeps
// the original result rather than surfacing an error.
export async function recognizeJapaneseText(source: File | Blob, options: RecognizeOptions = {}): Promise<OcrResult> {
    requireConnection();
    const primary = await recognizeJapaneseTextViaCloudOrLocal(source);

    if (!primary.lowConfidence || !options.getIdToken) return primary;

    try {
        const idToken = await options.getIdToken();
        const { text: rawText } = await recognizeJapaneseTextViaLlm(source, idToken);
        const cleaned = cleanCloudOcrText(rawText);
        if (!cleaned) return primary;

        // eslint-disable-next-line no-console
        console.info("[ocr] refined a low-confidence result using AI-assisted OCR");
        return { text: cleaned, confidence: primary.confidence, lowConfidence: false, aiAssisted: true };
    } catch (err) {
        // eslint-disable-next-line no-console
        console.info("[ocr] AI-assisted OCR refinement unavailable, keeping the original result:", err);
        return primary;
    }
}

async function recognizeJapaneseTextViaCloudOrLocal(source: File | Blob): Promise<OcrResult> {
    try {
        const { text: rawText, confidence } = await recognizeJapaneseTextViaCloud(source);
        // eslint-disable-next-line no-console
        console.info("[ocr] used Cloud Vision, confidence=%d", confidence);
        return {
            text: cleanCloudOcrText(rawText),
            confidence,
            lowConfidence: isLowConfidence(confidence),
        };
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[ocr] Cloud Vision unavailable, falling back to local Tesseract OCR:", err);
        return recognizeJapaneseTextLocally(source);
    }
}
